import { create } from "zustand";
import { reportSwallowed } from "../lib/reportSwallowed";
import type { FieldCovariate } from "../types/contract";
import {
  zonesFromLabels,
  buildZoneGeom,
  labelsFromServedZones,
  type ZoneGeom,
} from "../lib/zoneDesign";
import { nextFreeZoneId } from "../lib/nextZoneId";
import { designRelativeZones } from "../lib/relativeZoneDesign";
import {
  cleanZoneName,
  namesFromServedFc,
  stampZoneNames,
} from "../lib/zoneNames";
import { colorServedZoneFc } from "../lib/zoneColors";
import { resolveBehaviorDriverKeys } from "../lib/retestDecile";
import { behaviorColor } from "../lib/behaviorZones";
import {
  designDecileZones,
  rescaleDrivers,
  defaultZoneTarget,
  MOISTURE,
  PRODUCTIVITY,
  FERTILITY,
  type BehaviorDrivers,
} from "../lib/behaviorZones";
import {
  behaviorZoneTarget,
  dissolveBehaviorZones,
} from "../lib/behaviorZoneSession";
import { pickDesignCovariates } from "../lib/samplingDesign";
import { DEFAULT_ZONE_PRESET } from "../lib/relativePresets";
import {
  equalWeights,
  addWeight,
  removeWeight,
  redistributeWeights,
  weightMultiplier,
  clampK,
  type WeightMap,
} from "../lib/zoneEdit";
import { labelsWithOverrides, overridesFor } from "../lib/zoneCapture";
import {
  stackCellData,
  prefetchColumns,
  type CellGridId,
} from "../lib/stackClient";
import {
  scoreAccuracy,
  MODEL_KEY,
  ACCURACY_PROPS,
  type AccuracyResult,
  type AccProp,
} from "../lib/accuracyIndex";
import { acresOfGeoms } from "../lib/zoneAcres";
import { fetchAutomapFC } from "../lib/campaignOrder";
import { api } from "../api";

// The INTERACTIVE zone customize/edit session — drives ZoneEditor for BOTH schools: 'relative' =
// the Mahalanobis zones (checkbox-selected weighted layers + zone count k) 'behavior' = the
// 27-class zones (adjustable Dry/Mid/Wet · U/M/P · I/M/F thresholds) Both share the cell-paint
// editing model: the engine produces `computed` per-cell labels; manual brush edits live in
// `overrides` (cell → label) that always WIN, so re-tuning (weights/thresholds) and hand-edits
// coexist.
export type ZoneEditMode = "relative" | "behavior";
export type ZoneEditTool = "none" | "assign" | "new" | "splitline";
export type Breaks = [[number, number], [number, number], [number, number]];

interface Geo {
  geoms: { type: string; coordinates: unknown }[];
  geom: ZoneGeom;
  n: number;
}
export interface ZoneEditCapture {
  mode: ZoneEditMode;
  checked: string[];
  weights: WeightMap;
  k: number | null;
  breaks: Breaks;
  overrides: [number, number][]; // cell INDEX → label — see `grid`
  /*
   *
   * Without this the restore silently repainted DIFFERENT GROUND with the user's old edits.
   */
  grid?: CellGridId;
  /*
   *
   * behavior/decile only: the zone-count reducer's target; absent = full resolution.
   */
  zoneTarget?: number | null;
  names?: [number, string][];
}

// Re-deriving here at Customize re-derivation must use the same floor.
export const RELATIVE_MIN_ZONE_ACRES = 4;

// Cells are 8 m, so the default ~24 m is a 3-cell-wide dab and the range spans one cell to a broad
// sweep.
export const BRUSH_MIN_M = 8;
export const BRUSH_MAX_M = 120;
export const BRUSH_DEFAULT_M = 24;
export const BRUSH_STEP_M = 8; // one cell per click

const defaultBreaks = (): Breaks => [
  [...MOISTURE.breaks],
  [...PRODUCTIVITY.breaks],
  [...FERTILITY.breaks],
];
// It stays because multi-field capture/restore serializes it; a restore from a pre-decile capture
// must not crash, it just has nothing to apply the breaks to any more.

interface ZoneEditState {
  fieldId: string | null; // non-null = an interactive edit session is active for this field
  fieldName: string;
  mode: ZoneEditMode;
  campaign: boolean; // hide own Save/Cancel + defer to the CampaignStepBar when multi-field
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;

  geo: Geo | null;
  /* * identity of the loaded cell set, so a capture can be refused against a different one */
  grid: CellGridId | null;
  computed: number[]; // engine output per cell (zone id for relative / class idx for behavior)
  servedLabels: number[] | null;
  overrides: Map<number, number>; // manual cell → label (always win over `computed`)
  brushRadiusM: number;
  fc: GeoJSON.FeatureCollection | null;
  /* * a PICKED zone layer waiting to be adopted as the relative design's starting labels (retest customize) */
  pendingServedFc: GeoJSON.FeatureCollection | null;
  labelPoints: {
    zone: number;
    lng: number;
    lat: number;
    code?: string;
    name?: string;
  }[];
  names: Map<number, string>;

  // Soil Map Accuracy Index — recomputed on every redissolve (live as the user tweaks/paints)
  accuracy: AccuracyResult | null;
  modelSurfaces: Partial<Record<AccProp, (number | null)[]>>;
  areaAcres: number;

  // relative
  catalog: FieldCovariate[];
  checked: string[]; // selected covariate keys, in selection order
  weights: WeightMap; // key → weight %
  k: number | null;
  suggestedK: number | null;
  ev: number | null;

  // behavior
  breaks: Breaks;
  drivers: BehaviorDrivers | null;

  // editing
  tool: ZoneEditTool;
  target: number | null; // assign target zone id / class idx
  undo: [number, number][][]; // override-snapshots for undo
  designRev: number; // bumped on any design-affecting change → the component debounces recompute
  pendingRestore: ZoneEditCapture | null; // multi-field: a field's captured edit-state to re-apply on (re)load

  start: (
    fieldId: string,
    mode: ZoneEditMode,
    opts?: {
      fieldName?: string;
      campaign?: boolean;
      restore?: ZoneEditCapture;
      k?: number | null;
      boundaryAcres?: number;
      previewFc?: GeoJSON.FeatureCollection | null;
    },
  ) => void;
  end: () => void;
  load: () => Promise<void>;
  recompute: () => Promise<void>;
  redissolve: () => void;
  effectiveLabels: () => number[];
  setChecked: (key: string, on: boolean) => void;
  applyDesign: (checked: string[], weights: WeightMap) => void;
  setWeight: (key: string, pct: number) => void;
  setK: (k: number | null) => void;
  setBreak: (dim: 0 | 1 | 2, edge: 0 | 1, value: number) => void;
  setTool: (tool: ZoneEditTool, target?: number | null) => void;
  setTarget: (target: number | null) => void;
  paint: (cellIdx: number, label: number) => void;
  paintMany: (entries: [number, number][]) => void;
  pushUndo: () => void;
  setBrushRadiusM: (m: number) => void;
  undoPaint: () => void;
  nextZoneId: () => number;
  /*
   *
   * COMBINE ZONES: every cell of `labels` becomes ONE zone — the lowest label as a single undoable
   * stroke; the kept zone keeps its name.
   */
  combineZones: (labels: number[]) => number | null;
  zoneTarget: number | null;
  /* * true once a human (or a restore) chose the target — the acres÷22 default must never override them */
  zoneTargetTouched: boolean;
  /*
   *
   * Weight-only tweaks don't reset it: same layers, same claim.
   */
  kTouched: boolean;
  /*
   *
   * the FIELD's boundary acres — the acres÷22 default MUST use this, not the cell-area sum.
   * `areaAcres` (cell-sum) keeps its original jobs: accuracy tail + pricing/density readouts.
   */
  boundaryAcres: number | null;
  /* * labels as shown — post-reducer (behavior) / post-override (relative); the cell grid colors from these */
  shownLabels: number[] | null;
  setZoneTarget: (n: number | null) => void;
  /* * rename one zone; '' (or whitespace) restores its default. Re-stamps the fc in place — no dissolve */
  setZoneName: (label: number, name: string) => void;
  capture: () => ZoneEditCapture | null;
  restore: (cap: ZoneEditCapture) => Promise<void>;
}

// The DEFAULT zone-count target — boundary acres ÷ 22, always rounded UP single-sourced in
// behaviorZones so the preset chain and this store can never diverge again).
export { defaultZoneTarget };

const BASE = {
  status: "idle" as const,
  error: null as string | null,
  geo: null,
  pendingServedFc: null as GeoJSON.FeatureCollection | null,
  grid: null as CellGridId | null,
  computed: [] as number[],
  servedLabels: null as number[] | null,
  overrides: new Map<number, number>(),
  brushRadiusM: BRUSH_DEFAULT_M,
  fc: null,
  labelPoints: [] as {
    zone: number;
    lng: number;
    lat: number;
    code?: string;
    name?: string;
  }[],
  names: new Map<number, string>(),
  // The cell grid colors from THESE, so the pixels always match the outlines drawn from the same
  // array reducing zones moved the boundaries while the pixels kept full-resolution colors).
  shownLabels: null as number[] | null,
  accuracy: null as AccuracyResult | null,
  modelSurfaces: {} as Partial<Record<AccProp, (number | null)[]>>,
  areaAcres: 0,
  catalog: [] as FieldCovariate[],
  checked: [] as string[],
  weights: {} as WeightMap,
  k: null as number | null,
  suggestedK: null as number | null,
  ev: null as number | null,
  breaks: defaultBreaks(),
  drivers: null,
  zoneTarget: null as number | null,
  zoneTargetTouched: false,
  kTouched: false,
  boundaryAcres: null as number | null,
  tool: "none" as const,
  target: null as number | null,
  undo: [] as [number, number][][],
  designRev: 0,
  pendingRestore: null as ZoneEditCapture | null,
};

export const useZoneEditStore = create<ZoneEditState>((set, get) => ({
  fieldId: null,
  fieldName: "",
  mode: "relative",
  campaign: false,
  ...BASE,

  start: (fieldId, mode, opts) => {
    // seed the zone count from the preview's recommended number (opts.k) so relative Customize opens at it
    // instead of the k=2 fallback; load then prefers this seed over the (live-unimplemented) suggest endpoint.
    const seedK = clampK(opts?.k ?? null);
    // a seeded k (the served card's zone count) is a MEANINGFUL opening state: mark it touched so weight
    // tweaks keep it; the first layer/preset change hands control to the recommendation
    set({
      fieldId,
      mode,
      fieldName: opts?.fieldName ?? fieldId,
      campaign: opts?.campaign ?? false,
      ...BASE,
      boundaryAcres: opts?.boundaryAcres ?? null,
      overrides: new Map(),
      names: new Map(),
      breaks: defaultBreaks(),
      undo: [],
      pendingRestore: opts?.restore ?? null,
      k: seedK,
      suggestedK: seedK,
      kTouched: seedK != null,
    });
    if (opts?.previewFc?.features?.length) {
      set({
        fc: colorServedZoneFc(
          opts.previewFc,
          mode === "behavior" ? behaviorColor : null,
        ),
      });
      // relative mode also ADOPTS the picked design as its starting labels (see load)
      if (mode === "relative") set({ pendingServedFc: opts.previewFc });
    }
  },
  end: () =>
    set({
      fieldId: null,
      campaign: false,
      ...BASE,
      overrides: new Map(),
      names: new Map(),
      breaks: defaultBreaks(),
      undo: [],
      pendingRestore: null,
    }),

  load: async () => {
    const s = get();
    if (!s.fieldId) return;
    const fieldId = s.fieldId;
    set({ status: "loading", error: null });
    // race-guarded three ways: same field, still not ready, and nothing else has painted `fc` a
    // preview must never overwrite a computed result that finished first.
    if (!get().fc) {
      const mode0 = s.mode;
      void fetchAutomapFC(
        fieldId,
        mode0 === "behavior"
          ? "automap-decile-zones"
          : "automap-relative-zones",
      )
        .then((served) => {
          const now = get();
          if (
            now.fieldId === fieldId &&
            now.status === "loading" &&
            !now.fc &&
            served?.features?.length
          ) {
            set({
              fc: colorServedZoneFc(
                served,
                mode0 === "behavior" ? behaviorColor : null,
              ),
            });
          }
        })
        .catch(() => {
          /* a missing automap costs the stopgap, never the editor */
        });
    }
    try {
      const catalog = await api.fetchFieldCovariates(fieldId);
      if (get().fieldId !== fieldId) return; // session changed while loading
      if (s.mode === "relative") {
        const preset = DEFAULT_ZONE_PRESET.resolve(catalog);
        const checked = preset?.checked.length
          ? preset.checked
          : pickDesignCovariates(catalog);
        if (!checked.length)
          throw new Error(
            "No field data with values yet — zones need collected covariates (terrain/imagery).",
          );
        const cell = await stackCellData(fieldId, checked);
        if (get().fieldId !== fieldId) return;
        const geo: Geo = {
          geoms: cell.geoms,
          geom: buildZoneGeom(cell.geoms),
          n: cell.geoms.length,
        };
        set({ grid: cell.grid });
        const seededK = get().k; // seeded from the served preview count in start (the count the card showed)
        let fetchedK: number | null = null;
        try {
          const r = await api.suggestZoneCount(fieldId);
          fetchedK = clampK(r.zone_count);
        } catch (err) {
          reportSwallowed("suggest zone count", err);
        }
        // The same fetch also supplies the k FALLBACK (the served FC's feature count = the number
        // the preview shows) for the paths that don't pre-seed k: the multi-field campaign, or a
        // Customize click before the FC resolved.
        let servedLabels: number[] | null = null;
        try {
          // A PICKED zone layer — Order Retest customize takes the AutoMap's place as the design
          // the editor ADOPTS — same sticky `servedLabels` mechanism: the picked zones stay until a
          // preset / layer / weight / count change re-designs, which is exactly the "clicking any
          // preset discards the uploaded zones … otherwise edit the zones as-is" behaviour
          // specified.
          const amFc =
            get().pendingServedFc ??
            (await fetchAutomapFC(fieldId, "automap-relative-zones"));
          if (get().fieldId !== fieldId) return;
          servedLabels = labelsFromServedZones(geo.geom.centroids, amFc);
          if (servedLabels && !get().names.size)
            set({ names: namesFromServedFc(amFc) });
          if (fetchedK == null && amFc && amFc.features.length >= 2)
            fetchedK = clampK(amFc.features.length);
        } catch {
          /* no served AutoMap (or unreachable) → client design + designZones elbow auto-pick */
        }
        if (get().fieldId !== fieldId) return; // network calls above — re-check before writing
        // PREFER the seeded preview count so Customize matches the preset card even when the endpoint is down
        // else the endpoint's recommendation, else the served AutoMap count, else null → designZones elbow.
        const weights = preset?.checked.length
          ? preset.weights
          : equalWeights(checked);
        console.info(
          "[zone-editor] relative load · cells=%d · checked=%d · servedLabels=%s · k=%s",
          geo.n,
          checked.length,
          servedLabels ? servedLabels.length : "none",
          String(seededK ?? fetchedK),
        );
        set({
          catalog,
          checked,
          weights,
          geo,
          servedLabels,
          k: seededK ?? fetchedK,
          suggestedK: seededK ?? fetchedK,
        });
        // Warm only the DESIGN-RELEVANT columns (the checked set + the broad auto-pick pool, ~25
        // keys) NOT the whole catalog. "Prefetch everything" was the large-field tab killer "Aw
        // Snap" OOM): the catalog runs 1,000+ has_values rows now (every weekly-climate step is its
        // own row), and cells × columns row-object materialization is gigabytes on a big field.
        const warm = [
          ...new Set([...checked, ...pickDesignCovariates(catalog)]),
        ];
        void prefetchColumns(fieldId, warm).catch((err) =>
          reportSwallowed("prefetch zone columns", err),
        );
      } else {
        // the driver resolution lives in lib/retestDecile so the retest decile classifier and this
        // editor cannot resolve DIFFERENT columns for the same three axes.
        const { mKey, pKey, fKey } = resolveBehaviorDriverKeys(catalog);
        const cell = await stackCellData(fieldId, [mKey, pKey, fKey]);
        if (get().fieldId !== fieldId) return;
        const geo: Geo = {
          geoms: cell.geoms,
          geom: buildZoneGeom(cell.geoms),
          n: cell.geoms.length,
        };
        set({ grid: cell.grid });
        // NB: do NOT reset `breaks` here — start already set them, and stomping them would discard
        // any threshold the user edited while this async load was in flight.
        set({
          catalog,
          geo,
          drivers: rescaleDrivers({
            moisture: cell.features[0],
            productivity: cell.features[1],
            fertility: cell.features[2],
          }),
        });
      }
      const gArea = get().geo;
      if (gArea)
        set({
          areaAcres: acresOfGeoms(gArea.geoms),
        });
      // uncorrected model surfaces for the live Soil Map Accuracy Index — value-free, cheap
      try {
        const mh = await stackCellData(
          fieldId,
          ACCURACY_PROPS.map((p) => MODEL_KEY[p]),
        );
        if (get().fieldId !== fieldId) return;
        const surfaces: Partial<Record<AccProp, (number | null)[]>> = {};
        ACCURACY_PROPS.forEach((p, i) => {
          surfaces[p] = mh.features[i];
        });
        set({ modelSurfaces: surfaces });
      } catch {
        /* missing model surfaces → the index just flags low_confidence */
      }
      // multi-field: re-apply a field's captured edit-state (weights/thresholds + hand-edits) on re-entry
      const restore = get().pendingRestore;
      if (restore && restore.mode === get().mode) {
        set({
          // And a restored design must RE-DESIGN, not show the served one-shot over the captured
          // overrides.
          ...(restore.mode === "relative"
            ? {
                checked: restore.checked,
                weights: restore.weights,
                k: restore.k,
                kTouched: restore.k != null,
                servedLabels: null,
              }
            : {
                breaks: restore.breaks,
                zoneTarget: restore.zoneTarget ?? null,
                zoneTargetTouched: restore.zoneTarget != null,
              }),
          overrides: overridesFor(restore, get().grid),
          names: new Map(restore.names ?? []),
          pendingRestore: null,
        });
      } else if (restore) {
        set({ pendingRestore: null });
      }
      await get().recompute();
    } catch (e) {
      if (get().fieldId === fieldId)
        console.error("[zone-editor] load failed:", e);
      set({
        status: "error",
        error: e instanceof Error ? e.message : "Failed to load field data.",
      });
    }
  },

  recompute: async () => {
    const s = get();
    if (!s.fieldId || !s.geo) return;
    const fieldId = s.fieldId;
    // Any designRev bump while we were awaiting = a newer recompute owns the result; this one must
    // drop its answer.
    const rev = s.designRev;
    const stale = () => get().fieldId !== fieldId || get().designRev !== rev;
    try {
      if (s.mode === "relative") {
        const checked = s.checked;
        if (!checked.length) {
          set({
            status: "error",
            error: "Select at least one layer to design zones.",
          });
          return;
        }
        // STICKY, not one-shot Order-Retest customize: "it first loads the zone layer you chose
        // then the calculated zone layer overwrites it … It shouldn't regen unless you pick a
        // preset or change the selected layers/weights"): the adopted labels stay `computed` across
        // ANY later recompute until a DESIGN input changes — setChecked / applyDesign / setWeight /
        // setK / restore clear `servedLabels`, and only then does the layer design run.
        const served = s.servedLabels;
        if (served && served.length === s.geo.n) {
          set({ computed: served, ev: null });
          get().redissolve();
          return;
        }
        const cell = await stackCellData(fieldId, checked);
        if (stale()) return;
        const now = get();
        const designed = designRelativeZones({
          fieldId,
          checked,
          features: cell.features,
          geoms: s.geo.geoms,
          geom: s.geo.geom,
          weightOf: (key) =>
            weightMultiplier(now.weights[key] ?? 0, checked.length),
          kTouched: now.kTouched,
          k: now.k,
          boundaryAcres: now.boundaryAcres ?? undefined,
          minZoneAcres: RELATIVE_MIN_ZONE_ACRES,
        });
        set({
          computed: designed.computed,
          servedLabels: null,
          ev: designed.ev,
          suggestedK: designed.suggestedK,
          ...(designed.kSet ? { k: designed.k } : {}),
        });
      } else {
        const dv = s.drivers;
        if (!dv) return;
        // DECILES in customize: 10 percentile bins per driver, up to 1,000 combinations, named by
        // their decile coordinates.
        const res = designDecileZones({
          geoms: s.geo.geoms,
          moisture: dv.moisture,
          productivity: dv.productivity,
          fertility: dv.fertility,
        });
        set({ computed: res.labels });
        // Applied ONLY when nothing chose a target yet: a restore that carried one, or a user who
        // already touched the stepper, wins. Read after the computed write so a subscriber that
        // sets a target during that update still wins.
        const now = get();
        if (now.zoneTarget == null && !now.zoneTargetTouched) {
          const def = behaviorZoneTarget(
            res.labels,
            now.boundaryAcres ?? now.areaAcres,
          );
          if (def != null) set({ zoneTarget: def });
        }
      }
      get().redissolve();
    } catch (e) {
      if (get().fieldId === fieldId)
        set({
          status: "error",
          error: e instanceof Error ? e.message : "Could not compute zones.",
        });
    }
  },

  setZoneTarget: (n) => {
    set({ zoneTarget: n, zoneTargetTouched: true });
    get().redissolve();
  },

  setZoneName: (label, name) => {
    const s = get();
    const clean = cleanZoneName(name);
    const names = new Map(s.names);
    if (clean) names.set(label, clean);
    else names.delete(label);
    // re-stamp the CURRENT fc/labels — a rename is a string on the polygon, never a dissolve
    const named = stampZoneNames(s.fc, s.labelPoints, names);
    set({ names, fc: named.fc, labelPoints: named.labelPoints });
  },

  redissolve: () => {
    const s = get();
    if (!s.geo) return;
    const labels = get().effectiveLabels();
    let shown = labels;
    let out: {
      fc: GeoJSON.FeatureCollection;
      labelPoints: { zone: number; lng: number; lat: number; code?: string }[];
    } | null = null;
    if (s.mode === "relative")
      out = zonesFromLabels(s.geo.geoms, labels, { geom: s.geo.geom });
    else if (s.drivers) {
      const dissolved = dissolveBehaviorZones({
        geoms: s.geo.geoms,
        geom: s.geo.geom,
        labels,
        drivers: s.drivers,
        zoneTarget: s.zoneTarget,
      });
      shown = dissolved.shown;
      out = dissolved;
    }
    if (!out) return;
    // live accuracy index — both zone schools score as zone maps (§6); value-free, off the cell centroids
    const accuracy = scoreAccuracy({
      kind: "zones",
      cellLngLat: s.geo.geom.centroids,
      model: s.modelSurfaces,
      zones: out.fc,
      areaAcres: s.areaAcres,
    });
    console.info(
      "[zone-editor] computed · zones=%d · labels=%d",
      out.fc.features.length,
      out.labelPoints.length,
    );
    const named = stampZoneNames(out.fc, out.labelPoints, s.names);
    set({
      fc: named.fc,
      labelPoints: named.labelPoints,
      shownLabels: shown,
      accuracy,
      status: "ready",
      error: null,
    });
  },

  effectiveLabels: () => {
    const s = get();
    return labelsWithOverrides(s.computed, s.overrides);
  },

  setChecked: (key, on) =>
    set((s) => {
      if (on === s.checked.includes(key)) return s;
      const checked = on
        ? [...s.checked, key]
        : s.checked.filter((k) => k !== key);
      const weights = on
        ? addWeight(s.weights, key)
        : removeWeight(s.weights, key);
      // a k chosen against a DIFFERENT layer set is not a choice about this one — hand control back
      // to the recommendation — the missing half of the kTouched lifecycle, with setK's.
      return {
        checked,
        weights,
        kTouched: false,
        servedLabels: null,
        designRev: s.designRev + 1,
      };
    }),
  // bulk-set the whole design at once (a preset) — checked + weights together, drop any stale
  // hand-paint (a preset is a fresh design), then re-zone via the designRev bump.
  applyDesign: (checked, weights) =>
    set((s) => ({
      checked: [...checked],
      weights: { ...weights },
      overrides: new Map(),
      names: new Map(),
      servedLabels: null,
      designRev: s.designRev + 1,
      kTouched: false,
    })),
  setWeight: (key, pct) =>
    set((s) => ({
      weights: redistributeWeights(s.weights, key, pct),
      servedLabels: null,
      designRev: s.designRev + 1,
    })),
  // kTouched: the step IS a deliberate choice — without this, recompute derives the recommendation
  // and writes it straight back over the user's k — the stepper "flashed then reverted".
  setK: (k) =>
    set((s) => ({
      k: clampK(k),
      kTouched: true,
      servedLabels: null,
      designRev: s.designRev + 1,
    })),
  setBreak: (dim, edge, value) =>
    set((s) => {
      const breaks = s.breaks.map((b) => [...b]) as Breaks;
      breaks[dim][edge] = value;
      // keep low ≤ high on each dimension
      if (breaks[dim][0] > breaks[dim][1])
        breaks[dim][edge === 0 ? 1 : 0] = value;
      return { breaks, designRev: s.designRev + 1 };
    }),

  setTool: (tool, target) =>
    set({ tool, ...(target !== undefined ? { target } : {}) }),
  setTarget: (target) => set({ target }),

  pushUndo: () =>
    set((s) => ({ undo: [...s.undo.slice(-49), [...s.overrides.entries()]] })),
  setBrushRadiusM: (m: number) =>
    set({
      brushRadiusM: Math.max(BRUSH_MIN_M, Math.min(BRUSH_MAX_M, Math.round(m))),
    }),
  paint: (cellIdx, label) =>
    set((s) => {
      if (s.overrides.get(cellIdx) === label) return s;
      const overrides = new Map(s.overrides);
      overrides.set(cellIdx, label);
      return { overrides };
    }),
  paintMany: (entries) =>
    set((s) => {
      if (!entries.length) return s;
      const overrides = new Map(s.overrides);
      for (const [i, l] of entries) overrides.set(i, l);
      return { overrides };
    }),
  undoPaint: () => {
    const s = get();
    if (!s.undo.length) return;
    const prev = s.undo[s.undo.length - 1];
    set({ overrides: new Map(prev), undo: s.undo.slice(0, -1) });
    get().redissolve(); // re-cut the outlines/labels + rebuild the cell grid so the revert actually shows + saves
  },

  combineZones: (labels) => {
    const s = get();
    const chosen = [...new Set(labels)]
      .filter((l) => l >= 0)
      .sort((a, b) => a - b);
    if (chosen.length < 2 || !s.geo) return null;
    const keep = chosen[0]!;
    const gone = new Set(chosen.slice(1));
    const current = get().effectiveLabels();
    const entries: [number, number][] = [];
    for (let i = 0; i < current.length; i++)
      if (gone.has(current[i]!)) entries.push([i, keep]);
    if (!entries.length) return keep;
    // one undo step for the whole combine (a stroke), then the merged cells as ONE overrides update
    get().pushUndo();
    get().paintMany(entries);
    // the merged-away zones' names are dropped; the kept zone keeps its own (or its default)
    const names = new Map(get().names);
    for (const l of gone) names.delete(l);
    set({ names, tool: "none", target: null });
    get().redissolve();
    return keep;
  },

  nextZoneId: () => nextFreeZoneId(get().effectiveLabels()),

  capture: () => {
    const s = get();
    if (!s.geo) return null;
    // zoneTarget rides the capture: the type always declared it but capture never wrote it, so a
    // behavior-campaign field re-entry silently reset the user's chosen target to the acres÷22
    // default — exactly the "default must never override them" contract zoneTargetTouched exists
    // for.
    return {
      mode: s.mode,
      checked: s.checked,
      weights: s.weights,
      k: s.k,
      breaks: s.breaks.map((b) => [...b]) as Breaks,
      overrides: [...s.overrides.entries()],
      zoneTarget: s.zoneTarget ?? undefined,
      grid: s.grid ?? undefined,
      names: [...s.names.entries()],
    };
  },
  restore: async (cap) => {
    // kTouched: a restored k is a deliberate choice; servedLabels cleared so the restored design
    // RE-DESIGNS instead of adopting a pending served one-shot (same contract as load's pendingRestore)
    // names are keyed by LABEL, not cell index, so unlike overrides they need no grid check
    set({
      checked: cap.checked,
      weights: cap.weights,
      k: cap.k,
      kTouched: cap.k != null,
      servedLabels: null,
      breaks: cap.breaks,
      overrides: overridesFor(cap, get().grid),
      names: new Map(cap.names ?? []),
    });
    await get().recompute();
  },
}));
